'use client';

import React, { memo } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Bot, User, PhoneForwarded, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';

// --- Types ---
// Re-define or import the Message type if needed, mirroring ChatWidget
interface Message {
  role: 'user' | 'bot';
  text: string;
  followUpQuestions?: string[];
  showContactButton?: boolean;
}

// --- Custom Link Component (for Markdown) ---
// Copied from ChatWidget - consider moving to a shared utils file later
const MarkdownLink = memo(({ href, children }: { href?: string; children?: React.ReactNode }) => {
  if (!href) return children; 
  return (
    <a 
      href={href} 
      target="_blank" 
      rel="noopener noreferrer" 
      className="inline-flex items-center gap-1 text-primary underline hover:text-primary/90 transition-colors"
    >
      {children}
      <ExternalLink size={14} className="ml-0.5" />
    </a>
  );
});
MarkdownLink.displayName = 'MarkdownLink';

// --- Props Interface ---
interface ChatMessageProps {
  msg: Message;
  handleContactRedirect: () => void;
}

// --- Component Definition ---
const ChatMessageComponent: React.FC<ChatMessageProps> = ({ msg, handleContactRedirect }) => {
  return (
    <>
      <motion.div 
        className={cn(
          'flex items-end gap-2',
          msg.role === 'user' ? 'justify-end' : 'justify-start'
        )}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {msg.role === 'bot' && (
          <div className="w-9 h-9 rounded-full bg-muted dark:bg-gray-700 flex items-center justify-center shrink-0 overflow-hidden self-start mt-1"> 
            <img 
              src="/qopilot-avatar.png" 
              alt="QoPilot Avatar" 
              width={28} height={28}
              className="object-contain p-0.5"
            />
          </div>
        )}
        <div
          className={cn(
            'max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed shadow-sm',
            msg.role === 'user'
              ? 'bg-primary/90 dark:bg-primary text-primary-foreground rounded-tr-none'
              : 'bg-muted/70 dark:bg-gray-700/80 text-foreground rounded-tl-none border border-border/20 dark:border-gray-600/50 backdrop-blur-sm'
          )}
        >
          {msg.role === 'bot' ? (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: MarkdownLink,
                p: ({node, ...props}) => <p className="mb-1 last:mb-0" {...props} />,
                ol: ({node, ...props}) => <ol className="list-decimal list-outside pl-5 my-1 space-y-0.5" {...props} />,
                ul: ({node, ...props}) => <ul className="list-disc list-outside pl-5 my-1 space-y-0.5" {...props} />,
                li: ({node, ...props}) => <li className="leading-snug" {...props} />,
              }}
            >
              {msg.text}
            </ReactMarkdown>
          ) : (
            <p className="whitespace-pre-wrap">{msg.text}</p>
          )}
        </div>
        {msg.role === 'user' && (
          <div className="w-8 h-8 rounded-full bg-primary/20 dark:bg-blue-500/20 flex items-center justify-center shrink-0 shadow-sm self-start mt-1"> 
            <User size={16} className="text-primary dark:text-blue-300" />
          </div>
        )}
      </motion.div>

      {/* Contact Us Button (Rendered only if it's a bot message and flag is true) */}
      {msg.role === 'bot' && msg.showContactButton && (
        <div className="flex justify-start pl-11 mt-2 mb-2"> {/* Keep indentation consistent */} 
          <button
            onClick={handleContactRedirect}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary/10 text-primary rounded-lg border border-primary/20 hover:bg-primary/20 transition-colors shadow-sm"
          >
            <PhoneForwarded size={16} />
            Contact Our Team
          </button>
        </div>
      )}
    </>
  );
};

export const ChatMessage = memo(ChatMessageComponent); 